import { useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../lib/store';
import type { WorldBossCatalog, WorldBossFormDraft } from '../../lib/types';
import { Button, ErrorState, Modal, Panel } from '../ui';
import { formatDate, formatNumber, titleCase } from '../../lib/format';

/**
 * Admin → Events → World Boss → the form — design PART 40.
 *
 * **What this form does not contain is the point.** A World Boss *is* an
 * event, so it has no dates, no name, no summary, no theme and no reward
 * definitions of its own — those are authored on the Events tab and this picks
 * one. Duplicating them here would be two forms writing the same row, which is
 * the "two things that can disagree" mistake this codebase keeps naming.
 *
 * So the fields here are exactly the ones that describe *a fight*: who it is,
 * how hard it hits, how long an attempt lasts, how many attempts there are,
 * when a fighter locks, and which ranks get paid.
 *
 * **It decides nothing.** Every bound, default and option comes from
 * `/admin/world-boss/catalog`; the form renders them and posts intent. The
 * server validates the same values again with its own Zod schema, because a
 * disabled button is a courtesy and a schema is a rule.
 */

const INTEGER = /[^0-9]/g;

export function WorldBossForm({
  catalog,
  editing,
  onClose,
  onSaved,
}: {
  catalog: WorldBossCatalog;
  /** The boss being edited, or null to create one. */
  editing: WorldBossFormDraft | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const d = catalog.defaults;
  const [form, setForm] = useState<WorldBossFormDraft>(
    editing ?? {
      eventKey: '',
      speciesId: catalog.recommendedSpecies[1] ?? catalog.species[0]?.id ?? 145,
      title: '',
      intro: '',
      level: d.level,
      statScale: d.statScale,
      attackScale: d.attackScale,
      moves: [],
      encounterHp: d.encounterHp,
      encounterTurns: d.encounterTurns,
      phases: [],
      maxHp: 1_000_000,
      attemptsPerDay: d.attemptsPerDay,
      maxBonusAttempts: d.maxBonusAttempts,
      resetHour: d.resetHour,
      fighterLock: d.fighterLock,
      fighterLockHours: d.fighterLockHours,
      rewardFromRank: d.rewardFromRank,
      rewardToRank: d.rewardToRank,
      rewardDefKey: null,
      minContribution: 0,
      grantOnEscape: false,
      guestClaimDays: d.guestClaimDays,
      holdFlagged: true,
      finalizeMinutes: d.finalizeMinutes,
      milestones: [],
    },
  );

  const set = <K extends keyof WorldBossFormDraft>(key: K, value: WorldBossFormDraft[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const num = (key: keyof WorldBossFormDraft) => (raw: string) =>
    set(key, Number(raw.replace(INTEGER, '')) as never);

  const event = catalog.events.find((row) => row.key === form.eventKey) ?? null;
  const species = catalog.species.find((row) => row.id === form.speciesId) ?? null;

  // Only a POKEMON reward can be the reward zone's prize. Everything else on
  // the event is offered to the milestone ladder instead.
  const pokemonRewards = event?.rewards.filter((r) => r.kind === 'POKEMON') ?? [];
  const otherRewards = event?.rewards.filter((r) => r.kind !== 'POKEMON') ?? [];

  const legendaryCount = Math.max(0, form.rewardToRank - form.rewardFromRank + 1);
  const willMint = form.rewardDefKey ? legendaryCount : 0;

  /**
   * The warnings, computed here *and* again on the server.
   *
   * These are the ones an admin should see while they are still typing — the
   * server's copy is what makes them true, and the preview after saving states
   * them again. Nothing here blocks a save: PART 42 asks for scale to be made
   * obvious, not prevented.
   */
  const warnings = useMemo(() => {
    const out: string[] = [];
    if (!form.eventKey) out.push('Pick the event this fight belongs to.');
    if (event?.hasBoss && !editing) out.push(`${event.name} already has a fight. Saving will replace it.`);
    if (species && !species.isLegendary && !species.isMythical) {
      out.push(`${species.name} is not a Legendary. ${catalog.preseasonNote}`);
    }
    if (form.rewardToRank < form.rewardFromRank) out.push('The reward zone ends before it begins.');
    if (willMint > 0) {
      out.push(`This will create up to ${willMint} permanent ${species?.name ?? 'Legendary'}.`);
    }
    if (!form.rewardDefKey) {
      out.push('No reward configured — this fight pays nothing but milestones. Right for a preseason boss.');
    }
    if (form.attackScale > 1200) {
      out.push('A high attack scale removes fighters before they can swing. A boss is a wall, not an executioner.');
    }
    return out;
  }, [form, event, species, willMint, editing, catalog.preseasonNote]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/admin/world-boss', {
        ...form,
        // The server treats null and absent the same; sending the empty string
        // a <select> produces would fail its schema.
        rewardDefKey: form.rewardDefKey || null,
        stats: null,
      });
      push('success', editing ? 'Fight updated.' : 'Fight created.');
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save that fight.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} wide labelledBy="wbform-title">
      <div className="stack wb-form">
        <h2 id="wbform-title" className="page-head__title">
          {editing ? 'Edit fight' : 'New World Boss'}
        </h2>

        {error && <ErrorState message={error} />}

        {/* --- Which event, and which Pokemon. ------------------------------ */}
        <Panel title="The fight" raised={false}>
          <div className="wb-form__grid">
            <label>
              <span>Event</span>
              <select
                className="input"
                value={form.eventKey}
                onChange={(e) => set('eventKey', e.target.value)}
                disabled={Boolean(editing)}
              >
                <option value="">Choose an event…</option>
                {catalog.events.map((row) => (
                  <option key={row.key} value={row.key}>
                    {row.name} · {row.status}
                    {row.hasBoss ? ' · has a fight' : ''}
                  </option>
                ))}
              </select>
              {event && (
                <small>
                  {formatDate(event.startsAt)} → {formatDate(event.endsAt)}
                </small>
              )}
            </label>

            <label>
              <span>Boss species</span>
              <select
                className="input"
                value={form.speciesId}
                onChange={(e) => set('speciesId', Number(e.target.value))}
              >
                <optgroup label="Recommended (Gen 1–2)">
                  {catalog.species
                    .filter((row) => row.recommended)
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name} · {row.isMythical ? 'Mythical' : 'Legendary'} · BST {row.baseStatTotal}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Everything else imported">
                  {catalog.species
                    .filter((row) => !row.recommended)
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name} · BST {row.baseStatTotal}
                      </option>
                    ))}
                </optgroup>
              </select>
              {species && <small>{species.types.map(titleCase).join(' / ')}</small>}
            </label>

            <label>
              <span>Encounter name</span>
              <input
                className="input"
                value={form.title}
                placeholder="Stormfront"
                maxLength={80}
                onChange={(e) => set('title', e.target.value)}
              />
            </label>

            <label className="wb-form__wide">
              <span>Intro</span>
              <textarea
                className="input"
                rows={2}
                maxLength={500}
                value={form.intro}
                placeholder="Flavour shown above the sprite."
                onChange={(e) => set('intro', e.target.value)}
              />
            </label>
          </div>
        </Panel>

        {/* --- How hard it is. ---------------------------------------------- */}
        <Panel title="Difficulty" raised={false}>
          <div className="wb-form__grid">
            <Field label="Level" hint={`${catalog.limits.minLevel}–${catalog.limits.maxLevel}`}>
              <input className="input" value={form.level} onChange={(e) => num('level')(e.target.value)} />
            </Field>
            <Field label="Stat scale" hint="Permille over base stats. 1000 = unchanged.">
              <input className="input" value={form.statScale} onChange={(e) => num('statScale')(e.target.value)} />
            </Field>
            <Field label="Attack scale" hint="A second, gentler dial on offence. A wall, not an executioner.">
              <input className="input" value={form.attackScale} onChange={(e) => num('attackScale')(e.target.value)} />
            </Field>
            <Field label="Encounter turns" hint={`${catalog.limits.minEncounterTurns}–${catalog.limits.maxEncounterTurns}. One attempt's length.`}>
              <input className="input" value={form.encounterTurns} onChange={(e) => num('encounterTurns')(e.target.value)} />
            </Field>
            <Field label="Encounter HP" hint="Per attempt, not the shared pool.">
              <input className="input" value={form.encounterHp} onChange={(e) => num('encounterHp')(e.target.value)} />
            </Field>
            <Field label="Shared HP pool" hint="Run the simulation before setting this.">
              <input className="input" value={form.maxHp} onChange={(e) => num('maxHp')(e.target.value)} />
            </Field>
          </div>
        </Panel>

        {/* --- Attempts and the fighter lock. -------------------------------- */}
        <Panel title="Attempts and fighters" raised={false}>
          <div className="wb-form__grid">
            <Field label="Attempts per day" hint="Daily attempts do not carry over.">
              <input className="input" value={form.attemptsPerDay} onChange={(e) => num('attemptsPerDay')(e.target.value)} />
            </Field>
            <Field label="Max bonus attempts" hint="Stored attempts, which do carry over.">
              <input className="input" value={form.maxBonusAttempts} onChange={(e) => num('maxBonusAttempts')(e.target.value)} />
            </Field>
            <Field label="Daily reset hour (UTC)" hint="0–23.">
              <input className="input" value={form.resetHour} onChange={(e) => num('resetHour')(e.target.value)} />
            </Field>
            <Field label="Fighter lock">
              <select
                className="input"
                value={form.fighterLock}
                onChange={(e) => set('fighterLock', e.target.value)}
              >
                {catalog.lockModes.map((mode) => (
                  <option key={mode.key} value={mode.key}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </Field>
            {form.fighterLock === 'HOURS' && (
              <Field label="Lock hours">
                <input className="input" value={form.fighterLockHours} onChange={(e) => num('fighterLockHours')(e.target.value)} />
              </Field>
            )}
          </div>
        </Panel>

        {/* --- Who gets paid. ------------------------------------------------ */}
        <Panel title="Rewards" raised={false}>
          <div className="wb-form__grid">
            <Field label="Reward zone from rank">
              <input className="input" value={form.rewardFromRank} onChange={(e) => num('rewardFromRank')(e.target.value)} />
            </Field>
            <Field label="to rank">
              <input className="input" value={form.rewardToRank} onChange={(e) => num('rewardToRank')(e.target.value)} />
            </Field>
            <Field label="Prize" hint="A POKEMON reward on the chosen event.">
              <select
                className="input"
                value={form.rewardDefKey ?? ''}
                onChange={(e) => set('rewardDefKey', e.target.value || null)}
                disabled={!event}
              >
                <option value="">No prize — preseason fight</option>
                {pokemonRewards.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
              {event && pokemonRewards.length === 0 && (
                <small>
                  {event.name} has no Pokémon reward yet. Add one on the Events tab first.
                </small>
              )}
            </Field>
            <Field label="Minimum damage to qualify">
              <input className="input" value={form.minContribution} onChange={(e) => num('minContribution')(e.target.value)} />
            </Field>
            <Field label="Results window (minutes)" hint="Ranks freeze; nothing is minted until it elapses.">
              <input className="input" value={form.finalizeMinutes} onChange={(e) => num('finalizeMinutes')(e.target.value)} />
            </Field>
            <Field label="Guest claim window (days)">
              <input className="input" value={form.guestClaimDays} onChange={(e) => num('guestClaimDays')(e.target.value)} />
            </Field>
          </div>

          <label className="wb-form__check">
            <input
              type="checkbox"
              checked={form.grantOnEscape}
              onChange={(e) => set('grantOnEscape', e.target.checked)}
            />
            <span>
              Pay the prize even if the boss escapes
              <small>Off by default. The reward is for defeating it.</small>
            </span>
          </label>

          <label className="wb-form__check">
            <input
              type="checkbox"
              checked={form.holdFlagged}
              onChange={(e) => set('holdFlagged', e.target.checked)}
            />
            <span>
              Hold a flagged placing for review
              <small>A heuristic should never take somebody's Legendary on its own.</small>
            </span>
          </label>

          {/* The milestone ladder — PART 19. Everybody who is not in the top
              five still needs a reason to turn up. */}
          <Milestones
            rewards={otherRewards}
            value={form.milestones}
            onChange={(next) => set('milestones', next)}
            disabled={!event}
          />
        </Panel>

        {/* --- What this will do. -------------------------------------------- */}
        <div className="wb-admin__impact">
          <span className="tiny">Maximum new Legendaries this fight can create</span>
          <span className="wb-admin__impact-figure">{willMint}</span>
        </div>

        {warnings.map((warning) => (
          <p key={warning} className="wb-admin__warning">
            {warning}
          </p>
        ))}

        <div className="row row--between">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !form.eventKey}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create fight'}
          </Button>
        </div>

        <p className="tiny muted">
          Saving does not start anything. A fight opens when its event does, and publishing an event
          is still a separate decision on the Events tab.
        </p>
      </div>
    </Modal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label>
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

/**
 * The damage milestones.
 *
 * A list rather than a fixed set of rows, because PART 19 is explicit that
 * these are an event's choice and the ladder differs between a weekend fight
 * and an anniversary one. Each points at a reward that already exists on the
 * event, for the same reason the prize does.
 */
function Milestones({
  rewards,
  value,
  onChange,
  disabled,
}: {
  rewards: Array<{ key: string; label: string; kind: string }>;
  value: Array<{ damage: number; label: string; rewardDefKey: string }>;
  onChange: (next: Array<{ damage: number; label: string; rewardDefKey: string }>) => void;
  disabled: boolean;
}) {
  const update = (index: number, patch: Partial<(typeof value)[number]>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="stack-sm wb-form__milestones">
      <div className="row row--between">
        <strong className="tiny">Damage milestones</strong>
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled || rewards.length === 0 || value.length >= 12}
          onClick={() =>
            onChange([...value, { damage: 10_000, label: 'Milestone', rewardDefKey: rewards[0]!.key }])
          }
        >
          Add
        </Button>
      </div>

      {rewards.length === 0 ? (
        <p className="tiny muted">
          {disabled
            ? 'Choose an event first.'
            : 'This event has no non-Pokémon rewards to pay out. Add one on the Events tab.'}
        </p>
      ) : value.length === 0 ? (
        <p className="tiny muted">
          None. Everybody outside the reward zone takes home nothing — fine, but worth a thought.
        </p>
      ) : (
        value.map((row, index) => (
          <div key={index} className="row wb-form__milestone">
            <input
              className="input"
              value={row.damage}
              onChange={(e) => update(index, { damage: Number(e.target.value.replace(INTEGER, '')) })}
            />
            <input
              className="input"
              value={row.label}
              maxLength={60}
              onChange={(e) => update(index, { label: e.target.value })}
            />
            <select
              className="input"
              value={row.rewardDefKey}
              onChange={(e) => update(index, { rewardDefKey: e.target.value })}
            >
              {rewards.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
            <Button size="sm" variant="ghost" onClick={() => onChange(value.filter((_, i) => i !== index))}>
              ✕
            </Button>
          </div>
        ))
      )}

      {value.length > 0 && (
        <p className="tiny muted">
          Reached at {value.map((r) => formatNumber(r.damage)).join(', ')} damage.
        </p>
      )}
    </div>
  );
}
